import { useEffect, useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

// Exames Médicos (TMOTMED do ERP, frota.motmed — uma linha por funcionário).
// "Próximo" não é gravado: é a data + validade × 30 dias, como o ERP mostra.

interface Exame { data: string; validade: string; rotulo: string }
type Filtro = 'todos' | 'lancados' | 'vencidos'
type Situacao = { tipo: 'vazio' | 'sem-validade' | 'em-dia' | 'vencendo' | 'vencido'; prox: string }

const fmtData = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const proximo = (data: string, meses: string) => {
  const n = Number(meses) || 0
  if (!data || !n) return ''
  const d = new Date(`${data}T12:00:00`)
  d.setDate(d.getDate() + n * 30)
  return d.toISOString().slice(0, 10)
}
const somaDias = (iso: string, dias: number) => {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + dias)
  return d.toISOString().slice(0, 10)
}

const ESTILO_SITUACAO: Record<Situacao['tipo'], { bg: string; cor: string; rotulo: string }> = {
  'vazio': { bg: 'transparent', cor: '#9ca3af', rotulo: '' },
  'sem-validade': { bg: '#f3f4f6', cor: '#6b7280', rotulo: 'Sem validade' },
  'em-dia': { bg: '#ecfdf3', cor: '#15803d', rotulo: 'Em dia' },
  'vencendo': { bg: '#fffbeb', cor: '#b45309', rotulo: 'Vence em 30 dias' },
  'vencido': { bg: '#fef2f2', cor: '#b91c1c', rotulo: 'Vencido' },
}

// estilos só desta tela (inputs sem a borda preta padrão do navegador)
const CSS = `
.exm-input { border: 1px solid #d1d5db; border-radius: 6px; padding: 0.32rem 0.5rem; font-size: 0.82rem; color: #1f2937; background: #fff; outline: none; transition: border-color .15s, box-shadow .15s; }
.exm-input:hover:not(:disabled) { border-color: #9ca3af; }
.exm-input:focus { border-color: #1a6ea8; box-shadow: 0 0 0 3px rgba(26,110,168,.15); }
.exm-input:disabled { background: #f3f4f6; color: #9ca3af; }
.exm-quadro { flex: 1 1 300px; display: flex; align-items: center; justify-content: space-between; gap: .75rem; flex-wrap: wrap; border: 1px solid #c7d6ea; border-left: 4px solid #1b3f7b; border-radius: 10px; padding: .7rem .9rem; background: #eef3f9; }
.exm-quadro-titulo { font-size: .85rem; font-weight: 700; color: #1b3f7b; }
.exm-quadro-val { display: flex; align-items: center; gap: .5rem; font-size: .75rem; color: #6b7280; }
.exm-switch { display: flex; align-items: center; gap: .6rem; cursor: pointer; }
.exm-switch input { display: none; }
.exm-trilho { width: 34px; height: 20px; border-radius: 999px; background: #d1d5db; position: relative; transition: background .15s; flex-shrink: 0; }
.exm-trilho::after { content: ''; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.2); transition: left .15s; }
.exm-switch input:checked + .exm-trilho { background: #1a6ea8; }
.exm-switch input:checked + .exm-trilho::after { left: 16px; }
.exm-abas { display: inline-flex; background: #e2e8f0; border-radius: 8px; padding: 3px; gap: 2px; }
.exm-aba { border: none; background: transparent; color: #334155; font-size: .8rem; font-weight: 600; padding: .35rem .8rem; border-radius: 6px; cursor: pointer; }
.exm-aba:hover { color: #1b3f7b; }
.exm-aba.ativa { background: #1b3f7b; color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.15); }
.exm-qtd { font-size: .72rem; color: #64748b; margin-left: 2px; }
.exm-aba.ativa .exm-qtd { color: #bfdbfe; }
.exm-card { border: 1px solid #c7d6ea; border-radius: 10px; overflow: hidden; }
.exm-tabela { width: 100%; border-collapse: collapse; }
.exm-tabela th { text-align: left; font-size: .72rem; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: .04em; padding: .55rem .6rem; background: #1b3f7b; position: sticky; top: 0; z-index: 1; }
.exm-tabela td { padding: .4rem .6rem; border-bottom: 1px solid #dbe4ef; vertical-align: middle; }
.exm-tabela tbody tr:last-child td { border-bottom: none; }
.exm-tabela tbody tr:nth-child(even) { background: #eef3f9; }
.exm-tabela tbody tr:hover { background: #dde8f4; }
.exm-nome { font-size: .85rem; font-weight: 600; color: #1f2937; border-left: 4px solid transparent; }
.exm-tabela tr.vazio .exm-nome { font-weight: 500; color: #4b5563; }
.exm-meses { display: flex; align-items: center; gap: .4rem; font-size: .75rem; color: #6b7280; }
.exm-meses input { width: 52px; text-align: right; }
.exm-prox { font-size: .82rem; color: #374151; font-variant-numeric: tabular-nums; }
.exm-tabela tr.vazio .exm-prox { color: #d1d5db; }
.exm-sit { display: inline-flex; align-items: center; gap: .35rem; font-size: .75rem; font-weight: 600; padding: .2rem .55rem; border-radius: 999px; white-space: nowrap; }
.exm-ponto { width: 6px; height: 6px; border-radius: 50%; }
`

export default function FuncionarioExamesModal({ motcod, motnome, onClose }: { motcod: string; motnome: string; onClose: () => void }) {
  const { empresaCod } = useEmpresa()
  const [f, setF] = useState<Record<string, string> | null>(null)
  const [exames, setExames] = useState<Exame[]>([])
  const [tipesc, setTipesc] = useState('')
  const [filtro, setFiltro] = useState<Filtro>('todos')
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/exames`, { params: { empresa: empresaCod } })
    const d: Record<string, string> = {}
    for (const [k, v] of Object.entries(r.data)) if (k !== 'exames') d[k] = v === null || v === undefined ? '' : String(v)
    for (const e of r.data.exames as Exame[]) if (d[e.validade] === '0') d[e.validade] = ''
    setF(d)
    setExames(r.data.exames)
    setTipesc(String(r.data.tipesc ?? ''))
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar os exames do funcionário.'))
  }, [motcod])

  async function salvar() {
    setErro(''); setAviso('')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/exames`, f)
      await carregar()
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Exames salvos.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar os exames.')
    } finally {
      setSalvando(false)
    }
  }

  const set = (c: string, v: string) => setF(p => ({ ...p!, [c]: v }))

  const [gerandoPdf, setGerandoPdf] = useState(false)
  async function imprimir() {
    setErro(''); setAviso('')
    setGerandoPdf(true)
    try {
      const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/exames/pdf`, { responseType: 'blob' })
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank')
    } catch (e: any) {
      // erro vem como blob JSON
      let msg = 'Erro ao gerar a impressão dos exames.'
      try { msg = JSON.parse(await e?.response?.data?.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setGerandoPdf(false)
    }
  }

  // Carteirinha = Sim com parâmetro 'O': validade vem do próximo da Acuidade Visual (igual ao ERP)
  function mudarCarteirinha(v: string) {
    setF(p => {
      const novo: Record<string, string> = { ...p!, motmedcrt: v }
      if (v === 'S' && tipesc === 'O') {
        const ofd = proximo(p!.motmedoft, p!.motmedofv)
        if (ofd) novo.motmedva1 = ofd
      }
      return novo
    })
  }

  const hoje = new Date().toISOString().slice(0, 10)
  const em30 = somaDias(hoje, 30)
  const situacao = (e: Exame): Situacao => {
    if (!f?.[e.data]) return { tipo: 'vazio', prox: '' }
    const prox = proximo(f[e.data], f[e.validade])
    if (!prox) return { tipo: 'sem-validade', prox: '' }
    if (prox <= hoje) return { tipo: 'vencido', prox }
    if (prox <= em30) return { tipo: 'vencendo', prox }
    return { tipo: 'em-dia', prox }
  }

  const sits = exames.map(e => ({ e, s: situacao(e) }))
  const qtdLancados = sits.filter(x => x.s.tipo !== 'vazio').length
  const qtdVencidos = sits.filter(x => x.s.tipo === 'vencido').length
  const qtdVencendo = sits.filter(x => x.s.tipo === 'vencendo').length
  const visiveis = sits.filter(x =>
    filtro === 'todos' ? true : filtro === 'lancados' ? x.s.tipo !== 'vazio' : x.s.tipo === 'vencido' || x.s.tipo === 'vencendo')

  const aba = (k: Filtro, texto: string, qtd: number) => (
    <button key={k} className={`exm-aba${filtro === k ? ' ativa' : ''}`} onClick={() => setFiltro(k)}>
      {texto} <span className="exm-qtd">{qtd}</span>
    </button>
  )

  const quadro = (titulo: string, sn: string, onSn: (v: string) => void, campoVal: string) => (
    <div className="exm-quadro">
      <label className="exm-switch">
        <input type="checkbox" checked={sn === 'S'} onChange={e => onSn(e.target.checked ? 'S' : 'N')} />
        <span className="exm-trilho" />
        <span className="exm-quadro-titulo">{titulo}</span>
      </label>
      <div className="exm-quadro-val">
        <span>Validade</span>
        <input type="date" className="exm-input" value={f![campoVal]} disabled={sn !== 'S'}
          onChange={e => set(campoVal, e.target.value)} style={{ width: 140 }} />
      </div>
    </div>
  )

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <style>{CSS}</style>
      <div className="modal-box" style={{ maxWidth: 1000, maxHeight: '94vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}><h2>Exames Médicos — {motcod} · {motnome}</h2></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTMED</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!f ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.1rem' }}>
                {quadro('Carteirinha de saúde', f.motmedcrt, mudarCarteirinha, 'motmedva1')}
                {quadro('Cadastro ANTT', f.motmedant, v => set('motmedant', v), 'motmedanv')}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                <div className="exm-abas">
                  {aba('todos', 'Todos', exames.length)}
                  {aba('lancados', 'Lançados', qtdLancados)}
                  {aba('vencidos', 'Vencidos / vencendo', qtdVencidos + qtdVencendo)}
                </div>
                <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6b7280' }}>
                  Próximo = data + meses × 30 dias
                </span>
              </div>

              {visiveis.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280', padding: '1.5rem 0' }}>
                  {filtro === 'vencidos' ? 'Nenhum exame vencido ou vencendo.' : 'Nenhum exame lançado.'}
                </p>
              ) : (
                <div className="exm-card">
                <table className="exm-tabela">
                  <thead>
                    <tr><th>Exame</th><th>Data do exame</th><th>Validade</th><th>Próximo</th><th>Situação</th></tr>
                  </thead>
                  <tbody>
                    {visiveis.map(({ e, s }) => {
                      const est = ESTILO_SITUACAO[s.tipo]
                      return (
                        <tr key={e.data} className={s.tipo === 'vazio' ? 'vazio' : ''}>
                          <td className="exm-nome" style={{ borderLeftColor: s.tipo === 'vazio' ? '#cbd5e1' : est.cor }}>{e.rotulo}</td>
                          <td>
                            <input type="date" className="exm-input" value={f[e.data]} onChange={ev => set(e.data, ev.target.value)} style={{ width: 140 }} />
                          </td>
                          <td>
                            <div className="exm-meses">
                              <input className="exm-input" value={f[e.validade]} inputMode="numeric" maxLength={3} placeholder="0"
                                onChange={ev => set(e.validade, ev.target.value.replace(/\D/g, ''))} />
                              <span>meses</span>
                            </div>
                          </td>
                          <td className="exm-prox">{s.prox ? fmtData(s.prox) : '—'}</td>
                          <td>
                            {s.tipo !== 'vazio' && (
                              <span className="exm-sit" style={{ background: est.bg, color: est.cor }}>
                                <span className="exm-ponto" style={{ background: est.cor }} />{est.rotulo}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </>
          )}

          {f && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" style={{ marginRight: 'auto' }} onClick={imprimir} disabled={gerandoPdf || !f}
            title="Imprime os exames já salvos, com a foto do funcionário">
            {gerandoPdf ? 'Gerando...' : 'Imprimir'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !f}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
